import { useCallback, useEffect, useRef, useState } from 'react';
import {
  deleteTaskDocument,
  getTaskDocuments,
  getTaskDocumentUrl,
} from '../../services/api';
import { useToast } from '../../hooks/useToastContext';
import { useStore } from '../../store';

export interface TaskDocument {
  id: string;
  file_name: string;
}

/** Context a background upload needs to report where it came from. */
export interface TaskDocumentUploadTarget {
  /**
   * Owning patient. When supplied, a successful upload also refreshes the
   * patient page's Documents-count badge (via patientUploadTick).
   */
  patientId?: string;
  /**
   * Human-readable target for the upload progress toast ("{task} · {patient}").
   * Shown so a background upload stays identifiable after the surface that
   * started it closes or the user navigates away.
   */
  targetLabel?: string;
}

export interface UseTaskDocumentsResult {
  documents: TaskDocument[];
  loading: boolean;
  deleting: boolean;
  /** True once the list has been fetched at least once. */
  hasFetched: boolean;
  /**
   * True when the last fetch failed, so `documents` does not reflect what is
   * actually attached. Surfaces in the UI and gates signing: attesting to "any
   * document attached to this task" is only meaningful once we have seen them.
   */
  loadFailed: boolean;
  /**
   * True while at least one upload for this task is still in flight. Uploads are
   * fire-and-forget, so callers that sign the task must wait: signing now would
   * snapshot a document set the upload is about to change, or lock the task just
   * before it lands.
   */
  uploadPending: boolean;
  fetchDocuments: () => Promise<void>;
  openDocument: (documentId: string) => Promise<void>;
  /**
   * Hands the file to the background upload manager and returns immediately —
   * the store owns the progress toast and cancel, so callers must not block on
   * it. On success the store bumps this task's upload tick, which refreshes the
   * list here and fires `onDocumentsChanged`.
   */
  uploadDocument: (file: File) => void;
  deleteDocument: (documentId: string) => Promise<void>;
}

/**
 * Attached-document state for one task, shared by the Episode Manager row
 * popover and the misc task detail panel so both use the same
 * `/tasks/{task_id}/documents` calls and error copy.
 */
export function useTaskDocuments(
  taskId: string,
  onDocumentsChanged?: () => void,
  { patientId, targetLabel }: TaskDocumentUploadTarget = {}
): UseTaskDocumentsResult {
  const { error: showError } = useToast();
  const startTaskDocumentUpload = useStore(
    state => state.startTaskDocumentUpload
  );
  const uploadTick = useStore(state => state.taskUploadTick[taskId] ?? 0);
  const uploadPending = useStore(
    state => (state.taskUploadsInFlight[taskId] ?? 0) > 0
  );
  const [documents, setDocuments] = useState<TaskDocument[]>([]);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);

  // Switching tasks must not carry the previous task's list over. The Episode
  // Manager swaps `taskId` on the mounted detail panel rather than remounting
  // it, so without this reset `hasFetched` stays true across the switch. The
  // signing gate exists to prove the clinician saw this task's documents, and
  // it would instead be satisfied by the last task's.
  const prevTaskIdRef = useRef(taskId);
  if (prevTaskIdRef.current !== taskId) {
    prevTaskIdRef.current = taskId;
    setDocuments([]);
    setHasFetched(false);
    setLoadFailed(false);
  }

  // Monotonic per-request token. The reset above only clears state synchronously;
  // a fetch already in flight still resolves afterward, and its closure carries
  // the *previous* taskId. Applying it would repopulate the panel with the last
  // task's documents and re-satisfy the signing gate with the wrong set — the
  // exact failure the reset exists to prevent. So a response is applied only if
  // it is still the newest request AND still for the task on screen.
  const requestSeqRef = useRef(0);

  const fetchDocuments = useCallback(async () => {
    const seq = (requestSeqRef.current += 1);
    const requestedTaskId = taskId;
    const isCurrent = () =>
      requestSeqRef.current === seq &&
      prevTaskIdRef.current === requestedTaskId;

    setLoading(true);
    try {
      // throwOnError: the generated client defaults to false, so without it a
      // 4xx/5xx resolves with `data` undefined and never reaches the catch.
      // The caller would render "No documents attached" for a list it failed
      // to load, and let a clinician attest to documents they never saw.
      const { data } = await getTaskDocuments({
        path: { task_id: requestedTaskId },
        throwOnError: true,
      });
      if (!isCurrent()) return;
      setDocuments((data ?? []) as TaskDocument[]);
      setLoadFailed(false);
      setHasFetched(true);
    } catch (err) {
      // A superseded failure must stay silent too: flagging loadFailed here
      // would block signing on the new task because the old one errored.
      if (!isCurrent()) return;
      console.error('Failed to load task documents', err);
      setLoadFailed(true);
      showError('Failed to load documents. Please try again.');
    } finally {
      // The newest request owns the spinner; a superseded one must not clear it.
      if (isCurrent()) {
        setLoading(false);
      }
    }
  }, [taskId, showError]);

  // A background upload for this task just completed: refresh the list (if it
  // has ever loaded) and let the caller refresh its cached count. Guard on the
  // tick value itself so a change in the callback identity — or in `hasFetched`
  // — can't trigger a spurious refetch.
  const prevUploadTickRef = useRef(uploadTick);
  useEffect(() => {
    if (uploadTick === prevUploadTickRef.current) return;
    prevUploadTickRef.current = uploadTick;
    if (uploadTick === 0) return;
    if (hasFetched) {
      void fetchDocuments();
    }
    onDocumentsChanged?.();
  }, [uploadTick, hasFetched, fetchDocuments, onDocumentsChanged]);

  const openDocument = useCallback(
    async (documentId: string) => {
      try {
        // throwOnError, same reason as fetchDocuments: without it a 4xx/5xx
        // resolves with `data` undefined, so the click would silently do
        // nothing instead of telling the clinician the document is unreachable.
        const { data } = await getTaskDocumentUrl({
          path: { task_id: taskId, document_id: documentId },
          throwOnError: true,
        });
        if (!data?.url) {
          // 2xx with no presigned URL: still a failure to open, so it has to
          // reach the toast rather than fall through as a no-op.
          throw new Error('Document URL missing from response');
        }
        window.open(data.url, '_blank');
      } catch (err) {
        console.error('Failed to get document URL', err);
        showError('Failed to open document. Please try again.');
      }
    },
    [taskId, showError]
  );

  const uploadDocument = useCallback(
    (file: File) => {
      // Fire-and-forget: the store owns the upload + toast lifecycle and bumps
      // taskUploadTick on success, which the effect above turns into a refresh.
      void startTaskDocumentUpload({ taskId, patientId, file, targetLabel });
    },
    [startTaskDocumentUpload, taskId, patientId, targetLabel]
  );

  const deleteDocument = useCallback(
    async (documentId: string) => {
      setDeleting(true);
      try {
        await deleteTaskDocument({
          path: { task_id: taskId, document_id: documentId },
          throwOnError: true,
        });
        setDocuments(prev => prev.filter(doc => doc.id !== documentId));
        onDocumentsChanged?.();
      } catch (err) {
        console.error('Failed to delete document', err);
        showError('Failed to delete document. Please try again.');
      } finally {
        setDeleting(false);
      }
    },
    [taskId, onDocumentsChanged, showError]
  );

  return {
    documents,
    loading,
    deleting,
    hasFetched,
    loadFailed,
    uploadPending,
    fetchDocuments,
    openDocument,
    uploadDocument,
    deleteDocument,
  };
}
